import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditApplication, FullCreditApplication } from '../types'
import { Refinancing } from '../types/RefinancingSchema'
import { LoanRefinancingOutputDto } from './credit-application/LoanRefinancingInputDto'
import { fullCreditApplicationKeys } from './full-credit-application/full-credit-application-api'

// Query Keys
export const refinancingKeys = {
  all: ['refinancing'] as const,
  loans: (creditApplicationId: string) => [...refinancingKeys.all, 'loans', creditApplicationId] as const,
}

// API Functions
export async function getLoansForRefinancing(creditApplicationId: string): Promise<LoanRefinancingOutputDto[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<LoanRefinancingOutputDto[]>(
    `/Refinancing/GetLoansForRefinancing/${creditApplicationId}`,
  )
  return response.data
}

export async function updateRefinancing(data: Refinancing): Promise<CreditApplication> {
  const apiClient = getApiClient()
  const response = await apiClient.put<CreditApplication>(`/Refinancing/${data.id}/refinancing`, data)
  return response.data
}

// React-Query Hooks
export function useGetLoansForRefinancing(creditApplicationId: string, enabled = true): [LoanRefinancingOutputDto[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: refinancingKeys.loans(creditApplicationId),
    queryFn: () => getLoansForRefinancing(creditApplicationId),
    enabled: enabled && !!creditApplicationId,
  })
  return [data ?? [], isFetching]
}

export function useUpdateRefinancing(): [MutationFunction<CreditApplication, Refinancing>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateRefinancing,
    onSuccess: (updatedCreditApp) => {
      // Update the credit application within the fullCreditApplication cache
      const queryKey = fullCreditApplicationKeys.detail(updatedCreditApp.id, updatedCreditApp.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: updatedCreditApp,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}
